// Navigation & travel-mode icons — inline SVG, no emoji

interface IconProps {
  className?: string;
}

/* ---------- Travel modes ---------- */

export function CarIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 16.5h14M4.2 16.5v1.8a.9.9 0 0 1-.9.9H2.6a.9.9 0 0 1-.9-.9v-1.8" />
      <path d="M22.3 16.5v1.8a.9.9 0 0 1-.9.9h-.7a.9.9 0 0 1-.9-.9v-1.8" />
      <path d="M2.2 16.5v-3.2c0-.6.2-1.1.5-1.6l1.6-2.4A2 2 0 0 1 5.9 8.3h12.2c.6 0 1.1.3 1.6.8l1.6 2.4c.3.5.5 1 .5 1.6v3.4z" />
      <path d="M4.8 12.5h14.4" />
      <circle cx="7.4" cy="14.8" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.6" cy="14.8" r="1" fill="currentColor" stroke="none" />
      <path d="M6.2 8.3 7.6 5.6h8.8l1.4 2.7" opacity={0.6} />
    </svg>
  );
}

export function MotorbikeIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="17" r="3.2" />
      <circle cx="19" cy="17" r="3.2" />
      <path d="M8.2 17h2.4l2.6-5.2h-2.3" />
      <path d="M13.2 11.8 16 17" />
      <path d="M11 6.5h3.2l2.2 3.4" />
      <path d="M13.5 11.8 11.2 8.6h-1.4" opacity={0.65} />
      <path d="M9.4 6.5 8 9.8" opacity={0.5} />
    </svg>
  );
}

export function BikeIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5.4" cy="17" r="3.4" />
      <circle cx="18.6" cy="17" r="3.4" />
      <path d="M8.8 17h4.4l3.6-8h-2.4" />
      <path d="M12.4 9 10 6.5" />
      <path d="M15.2 6.5h3.2" />
      <path d="M13.2 17 10 9" opacity={0.6} />
      <path d="M8.6 6.5h2.8" />
    </svg>
  );
}

export function WalkIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13" cy="3.8" r="1.9" />
      <path d="M10.6 21.5 12 16l-2.6-2.6.8-5.2" />
      <path d="M12 16l3 2.4.6 3.1" />
      <path d="M10.2 8.2 8 10.4l-.6 3" />
      <path d="M12.4 9.2 15 11.4l.4 2.8" />
      <path d="M10.2 8.2 12.6 7l2.4 2.4" opacity={0.65} />
    </svg>
  );
}

/* ---------- Route endpoints ---------- */

// Origin pin (filled circle)
export function OriginMarkerIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
      <circle cx="12" cy="12" r="6.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="9" opacity={0.35} />
    </svg>
  );
}

// Destination pin (with inner dot)
export function DestinationMarkerIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
      <path d="M12 21.5s7-6.1 7-11.1a7 7 0 1 0-14 0c0 5 7 11.1 7 11.1z" />
      <circle cx="12" cy="10" r="2.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Accuracy accuracy circle
export function AccuracyIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
      <circle cx="12" cy="12" r="8.5" strokeDasharray="2.5 2.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ---------- Turn-by-turn maneuver arrows ---------- */

export function TurnLeftIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.5 20v-6a4 4 0 0 0-4-4H8" />
      <path d="M11 6.5 7 10l4 3.5" />
    </svg>
  );
}

export function TurnRightIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 20v-6a4 4 0 0 1 4-4H16" />
      <path d="M13 6.5 17 10l-4 3.5" />
    </svg>
  );
}

export function TurnSlightLeftIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 20v-7.5a3.5 3.5 0 0 1 3.5-3.5h.5" />
      <path d="M15 6 11.5 9.5 15 13" />
    </svg>
  );
}

export function TurnSlightRightIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 20v-7.5A3.5 3.5 0 0 1 6 9h-.5" />
      <path d="M9 6 12.5 9.5 9 13" />
    </svg>
  );
}

export function StraightIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20V6" />
      <path d="M8.5 9.5 12 6l3.5 3.5" />
    </svg>
  );
}

export function UTurnIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 20v-8a5 5 0 0 1 10 0v8" />
      <path d="M4.5 17 7 20l2.5-3" />
      <path d="M17 20l2.5-3" />
    </svg>
  );
}

export function MergeIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3.5v5a4 4 0 0 0 4 4h8" />
      <path d="M15 9.5 18.5 12.5 15 15.5" />
    </svg>
  );
}

export function RoundaboutIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13" r="4.2" />
      <path d="M12 3.5v5.3" />
      <path d="M15.5 7.5 18 10" />
      <path d="M18 10h-2.2" opacity={0.6} />
    </svg>
  );
}

export function FlagIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 21V4" />
      <path d="M5 4.5h11l-1.6 3.5L16 11.5H5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function StartIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ---------- Place info kinds ---------- */

// Generic POI — map pin with a dot
export function PlaceIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21.5s7-6.1 7-11.1a7 7 0 1 0-14 0c0 5 7 11.1 7 11.1z" />
      <circle cx="12" cy="10" r="2.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Road — two parallel lines with dashes
export function RoadIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
      <path d="M7 3v18M17 3v18" />
      <path d="M12 4v2.5M12 10.7v2.5M12 17.5V20" strokeDasharray="0.1 2.4" strokeWidth={2} />
    </svg>
  );
}

// Water — wave
export function WaterIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
      <path d="M2 8.5c2-2 3.6-2 5.5 0s3.5 2 5.5 0 3.6-2 5.5 0" />
      <path d="M2 13c2-2 3.6-2 5.5 0s3.5 2 5.5 0 3.6-2 5.5 0" />
      <path d="M2 17.5c2-2 3.6-2 5.5 0s3.5 2 5.5 0 3.6-2 5.5 0" />
    </svg>
  );
}

// Park — tree
export function ParkIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21v-5" />
      <path d="M12 3 6.5 11h3L5.5 16.5h13L14.5 11h3z" />
    </svg>
  );
}

// Mountain peak
export function MountainIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 19h20" />
      <path d="M2 19l6.5-11 4.5 7.5" />
      <path d="M12 19l5-8.5 5 8.5" />
      <path d="M17.5 3.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Building
export function BuildingIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 21V6.5L11 3v18" />
      <path d="M11 21V10l9 3v8" />
      <path d="M2.5 21h19" />
      <path d="M6.8 9.2h0M6.8 12.6h0M6.8 16h0M14.6 15h0M17.4 15h0M14.6 17.8h0M17.4 17.8h0" opacity={0.65} />
    </svg>
  );
}

// House number / address
export function HashIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
      <path d="M5 9h14M5 15h14" />
      <path d="M10 3.5 8 20.5M16 3.5l-2 17" />
    </svg>
  );
}

// Aerodrome / airport
export function AirplaneIcon({ className = 'w-5 h-5' }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.5 3.5a1.5 1.5 0 0 1 3 0V9l7.5 4.2v2.3L13.5 13v4.2l2.6 1.6V20.5L12 19l-4.1 1.5v-1.7l2.6-1.6V13L3 15.5v-2.3L10.5 9z" />
    </svg>
  );
}

/** Pick a maneuver icon from an OSRM step */
export function maneuverIcon(type: string, modifier: string) {
  const isLeft = modifier.includes('left');
  const isRight = modifier.includes('right');

  switch (type) {
    case 'depart':
      return StartIcon;
    case 'arrive':
      return FlagIcon;
    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
    case 'rotary turn':
      return RoundaboutIcon;
    case 'merge':
    case 'on ramp':
    case 'off ramp':
      return MergeIcon;
    case 'u-turn':
      return UTurnIcon;
    case 'continue':
      return StraightIcon;
    case 'fork':
      return StraightIcon;
    default:
      if (isLeft) return TurnLeftIcon;
      if (isRight) return TurnRightIcon;
      return StraightIcon;
  }
}